// Published for answers: the version in use, what the next publish would read in full against
// the budget, what's ready to publish, and earlier versions to go back to.

import { useState } from "react";
import type { CorpusVersion } from "@ask-hr/core/documents";
import { api } from "../api.js";
import { k, when } from "./documents.js";

type VersionSummary = Omit<CorpusVersion, "entries">;
interface Named {
  id: string;
  name: string;
}
export interface CorpusInfo {
  current: (VersionSummary & { pointer: { version: number; setBy: string; setAt: string } }) | null;
  versions: VersionSummary[];
  pending: { added: Named[]; updated: Named[]; removed: Named[] };
  next?: { tokens: CorpusVersion["tokens"]; alwaysTokens: CorpusVersion["tokens"] };
  approvedCount: number;
  budget: number;
}

// act: the page's action runner (status line, errors, reload); key names the busy button.
export function PublishPanel({
  corpus,
  busy,
  isAdmin,
  act,
}: {
  corpus: CorpusInfo | null;
  busy: string | null;
  isAdmin: boolean;
  act: (key: string, fn: () => Promise<unknown>, done: string) => Promise<void>;
}) {
  const [confirmPublish, setConfirmPublish] = useState(false);
  const pending = corpus?.pending;
  const pendingCount = pending ? pending.added.length + pending.updated.length + pending.removed.length : 0;
  return (
    <section className="panel" aria-labelledby="publish-h">
      <h2 id="publish-h">Published for answers</h2>
      {corpus?.current ? (
        <p>
          <strong>Version {corpus.current.version}</strong>
          <span className="muted">
            {" "}
            · {corpus.current.documentCount} documents · published {when(corpus.current.publishedAt)} by{" "}
            {corpus.current.publishedBy}
            {corpus.current.pointer.setAt !== corpus.current.publishedAt
              ? ` · made current ${when(corpus.current.pointer.setAt)} by ${corpus.current.pointer.setBy}`
              : ""}
          </span>
        </p>
      ) : (
        <p className="sub">Nothing is published yet. Ask HR has no documents to answer from until you publish.</p>
      )}
      {corpus?.next && corpus.approvedCount ? <Budget next={corpus.next} budget={corpus.budget} isAdmin={isAdmin} /> : null}

      {pending && pendingCount > 0 ? (
        <div className="pending">
          <p>
            <strong>Ready to publish:</strong> {listNames("added", pending.added)}
            {listNames("updated", pending.updated)}
            {listNames("removed", pending.removed)}
          </p>
          {confirmPublish ? (
            <div className="actions start">
              <button
                type="button"
                className="primary"
                disabled={busy !== null}
                onClick={() =>
                  void act("publish", () => api("POST", "/api/admin/corpus/publish"), "Published. Ask HR now answers from the new version.").then(
                    () => setConfirmPublish(false),
                  )
                }
              >
                {busy === "publish" ? "Publishing…" : `Publish ${corpus?.approvedCount ?? 0} approved documents`}
              </button>
              <button type="button" onClick={() => setConfirmPublish(false)}>
                Cancel
              </button>
            </div>
          ) : (
            <button type="button" className="primary" onClick={() => setConfirmPublish(true)}>
              Publish…
            </button>
          )}
        </div>
      ) : corpus ? (
        <p className="sub">{corpus.approvedCount ? "Everything approved is published." : "Approve documents below, then publish them here."}</p>
      ) : null}

      {corpus && corpus.versions.length > 1 ? (
        <details className="sec inner">
          <summary>
            <h3>Earlier versions</h3>
          </summary>
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Version</th>
                  <th scope="col">Published</th>
                  <th scope="col">Documents</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {corpus.versions.map((v) => (
                  <tr key={v.version}>
                    <td>{v.version}</td>
                    <td className="muted">
                      {when(v.publishedAt)} · {v.publishedBy}
                    </td>
                    <td>{v.documentCount}</td>
                    <td className="right">
                      {v.version === corpus.current?.version ? (
                        <span className="pill">In use</span>
                      ) : (
                        <button
                          type="button"
                          disabled={busy !== null}
                          onClick={() =>
                            void act(
                              `v${v.version}`,
                              () => api("POST", "/api/admin/corpus/rollback", { version: v.version }),
                              `Ask HR now answers from version ${v.version}.`,
                            )
                          }
                        >
                          Use version {v.version}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      ) : null}
    </section>
  );
}

function listNames(what: string, items: Named[]) {
  if (!items.length) return null;
  const names = items.slice(0, 5).map((i) => i.name).join(", ");
  return (
    <span className="pending-group">
      {items.length} {what} <span className="muted">({names}{items.length > 5 ? `, and ${items.length - 5} more` : ""})</span>{" "}
    </span>
  );
}

// What every question carries in full: the always-included documents, as the next publish would
// have them, against the limit. A warning only: publishing still goes ahead.
function Budget({ next, budget, isAdmin }: { next: NonNullable<CorpusInfo["next"]>; budget: number; isAdmin: boolean }) {
  const rows = [
    ["California", "us-ca"],
    ["US outside California", "us-other"],
    ["Canada", "canada"],
  ] as const;
  const max = Math.max(...rows.map(([, a]) => next.alwaysTokens[a]));
  const level = max > budget ? "over" : max > budget * 0.8 ? "near" : "ok";
  return (
    <div className={`budget ${level}`}>
      <p className="small">
        <strong>Read in full with every question</strong> <span className="muted">(as the next publish would be)</span>
      </p>
      <ul className="budget-rows">
        {rows.map(([label, a]) => {
          const n = next.alwaysTokens[a];
          const relevant = next.tokens[a] - n;
          return (
            <li key={a}>
              <span className="budget-label">{label}</span>
              <span className="budget-bar" aria-hidden="true">
                <span style={{ width: `${Math.min(100, (n / budget) * 100)}%` }} />
              </span>
              <span className="small">
                {k(n)} of {k(budget)}
                {relevant > 0 ? <span className="muted"> · {k(relevant)} in relevant parts</span> : null}
              </span>
            </li>
          );
        })}
      </ul>
      {level !== "ok" ? (
        <p className="small">
          <strong>{level === "over" ? "Over the limit." : "Close to the limit."}</strong>{" "}
          {level === "over" ? "Answers are slower and cost more until it's under." : ""}
          {isAdmin
            ? "Set the coverage of long documents that few questions need (benefits detail, notices, posters) to Relevant parts."
            : "An Ask HR admin can set the coverage of long documents to Relevant parts."}
        </p>
      ) : null}
    </div>
  );
}
